import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import {
  getAthletePlans,
  createPlan,
  updatePlan,
  disablePlan,
  type PlanDefinition,
  type BillingPeriod,
} from "../../services/plans";

type FormState = {
  code: string;
  name: string;
  description: string;
  priceEuros: string; // MONTHLY price, edited in euros, sent as cents
  priceYearEuros: string; // optional ANNUAL price, in euros
  billing_period: BillingPeriod; // "month" (paid) | "none" (free)
  features: string; // one per line
  sort_order: number;
  is_active: boolean;
};

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  description: "",
  priceEuros: "",
  priceYearEuros: "",
  billing_period: "month",
  features: "",
  sort_order: 0,
  is_active: true,
};

function euros(cents: number, currency: string) {
  return `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

export default function AthletePlans() {
  const { t } = useTranslation();
  const { activeRole } = useAuth();
  const isPlatformAdmin = activeRole === "platform_admin";

  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PlanDefinition | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setPlans(await getAthletePlans());
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut încărca planurile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPlatformAdmin) load();
    else setLoading(false);
  }, [isPlatformAdmin]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEdit = (p: PlanDefinition) => {
    setEditing(p);
    setForm({
      code: p.code,
      name: p.name,
      description: p.description || "",
      priceEuros: p.price_cents ? (p.price_cents / 100).toString() : "",
      priceYearEuros: p.price_cents_year ? (p.price_cents_year / 100).toString() : "",
      billing_period: p.billing_period === "none" ? "none" : "month",
      features: (p.features || []).join("\n"),
      sort_order: p.sort_order,
      is_active: p.is_active,
    });
    setError(null);
    setShowModal(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const isFree = form.billing_period === "none";
    const price_cents = isFree ? 0 : Math.max(0, Math.round((parseFloat(form.priceEuros) || 0) * 100));
    // Annual is optional: empty/0 → no yearly option (null).
    const price_cents_year =
      isFree || !form.priceYearEuros.trim()
        ? null
        : Math.max(0, Math.round((parseFloat(form.priceYearEuros) || 0) * 100)) || null;
    const features = form.features
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      if (editing) {
        await updatePlan(editing.id, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          price_cents,
          price_cents_year,
          billing_period: form.billing_period,
          features,
          sort_order: form.sort_order,
          is_active: form.is_active,
        });
      } else {
        await createPlan({
          audience: "athlete",
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description.trim() || null,
          price_cents,
          price_cents_year,
          currency: "EUR",
          billing_period: form.billing_period,
          features,
          sort_order: form.sort_order,
          is_active: form.is_active,
        });
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Salvarea a eșuat.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p: PlanDefinition) => {
    try {
      if (p.is_active) await disablePlan(p.id);
      else await updatePlan(p.id, { is_active: true });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Operațiunea a eșuat.");
    }
  };

  if (!isPlatformAdmin) {
    return (
      <div className="p-6">
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          {t("common.platformAdminOnly", "Doar administratorii platformei au acces la această pagină.")}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <AdminPageHeader
        eyebrow="Planuri"
        title="Planuri sportivi"
        subtitle="Definește planurile pe care le aleg părinții și sportivii. Prețul se sincronizează automat cu Stripe."
        actions={<AdminButton onClick={openCreate}>+ Adaugă plan</AdminButton>}
      />

      {error && !showModal && (
        <div className="mb-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-slate-400 py-10 text-center">Se încarcă…</div>
      ) : plans.length === 0 ? (
        <div className="p-8 text-center rounded-2xl bg-white border border-slate-200">
          <p className="text-slate-500">Niciun plan încă. Adaugă primul plan de sportiv.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-4 ${
                p.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-70"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-800">{p.name}</span>
                  <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{p.code}</span>
                  {!p.is_active && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                      Dezactivat
                    </span>
                  )}
                  {p.billing_period !== "none" &&
                    (p.stripe_price_id ? (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        Stripe ✓
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Fără Stripe
                      </span>
                    ))}
                </div>
                {p.description && <p className="text-sm text-slate-500 mt-0.5">{p.description}</p>}
                {p.features && p.features.length > 0 && (
                  <ul className="mt-1.5 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                    {p.features.map((f, i) => (
                      <li key={i}>· {f}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-extrabold text-slate-800 tabular-nums">
                  {p.billing_period === "none" ? "Gratuit" : euros(p.price_cents, p.currency)}
                </div>
                {p.billing_period !== "none" && <div className="text-xs text-slate-400">/lună</div>}
                {p.price_cents_year ? (
                  <div className="mt-1 text-xs text-slate-500 tabular-nums">
                    {euros(p.price_cents_year, p.currency)} <span className="text-slate-400">/an</span>
                  </div>
                ) : (
                  p.billing_period !== "none" && <div className="mt-1 text-[11px] text-slate-300">fără variantă anuală</div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => openEdit(p)}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Editează
                </button>
                <button
                  onClick={() => toggleActive(p)}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg border ${
                    p.is_active
                      ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                      : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  {p.is_active ? "Dezactivează" : "Activează"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowModal(false)} />
          <form
            onSubmit={save}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6"
          >
            <h3 className="text-lg font-bold text-slate-800 mb-4">
              {editing ? "Editează planul" : "Adaugă plan de sportiv"}
            </h3>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">{error}</div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-sm">
                <span className="font-semibold text-slate-700">Nume *</span>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="ex. Campion"
                />
              </label>
              <label className="text-sm">
                <span className="font-semibold text-slate-700">Cod {editing ? "" : "*"}</span>
                <input
                  value={form.code}
                  disabled={!!editing}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 font-mono text-sm"
                  placeholder="ex. campion"
                />
                <span className="text-[11px] text-slate-400">{editing ? "Codul nu se poate schimba" : "litere/cifre, ex. campion"}</span>
              </label>
              <label className="text-sm">
                <span className="font-semibold text-slate-700">Tip plan</span>
                <select
                  value={form.billing_period}
                  onChange={(e) => setForm({ ...form, billing_period: e.target.value as BillingPeriod })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                >
                  <option value="month">Plătit</option>
                  <option value="none">Gratuit (fără plată)</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="font-semibold text-slate-700">Ordine</span>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </label>
              <label className="text-sm">
                <span className="font-semibold text-slate-700">Preț lunar (EUR)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.priceEuros}
                  disabled={form.billing_period === "none"}
                  onChange={(e) => setForm({ ...form, priceEuros: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                  placeholder="ex. 9.99"
                />
              </label>
              <label className="text-sm">
                <span className="font-semibold text-slate-700">Preț anual (EUR)</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.priceYearEuros}
                  disabled={form.billing_period === "none"}
                  onChange={(e) => setForm({ ...form, priceYearEuros: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                  placeholder="opțional, ex. 99.00"
                />
                <span className="text-[11px] text-slate-400">Lasă gol dacă planul nu are variantă anuală.</span>
              </label>
              <label className="col-span-2 text-sm">
                <span className="font-semibold text-slate-700">Descriere</span>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                  placeholder="Un rând scurt despre plan"
                />
              </label>
              <label className="col-span-2 text-sm">
                <span className="font-semibold text-slate-700">Beneficii (unul pe rând)</span>
                <textarea
                  rows={4}
                  value={form.features}
                  onChange={(e) => setForm({ ...form, features: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 font-mono text-xs"
                  placeholder={"5 vieți\nRaport lunar\nFără reclame"}
                />
              </label>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 accent-indigo-600"
                />
                <span className="text-slate-700">Activ (vizibil pe site)</span>
              </label>
            </div>

            <p className="mt-3 text-[11px] text-slate-400">
              La salvare, prețul planurilor plătite se sincronizează cu un Produs + Preț recurent în Stripe. Planurile
              gratuite nu folosesc Stripe.
            </p>

            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold disabled:opacity-60"
              >
                {saving ? "Se salvează…" : editing ? "Salvează" : "Creează planul"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
